import { useDebounceFn } from '@vueuse/core';
import { type StopHandle, taggedLogger } from '@/util';

const log = taggedLogger('3D');

export type ElementSize = Readonly<{ width: number, height: number }>;

/**
 * Observe the size of an HTML element. When it changes, invoke the given callback.
 *
 * Providing a value for the debounce option in milliseconds will debounce invocations
 * of the callback.
 */
export function observeElementSize(
    element: HTMLElement,
    callback: (size: ElementSize, oldSize?: ElementSize) => void,
    options?: { debounce?: number },
): StopHandle {
    // Current size of the element
    let currentSize: ElementSize = { width: 0, height: 0 };

    function sizeFromEntry(entry: ResizeObserverEntry): ElementSize {
        const { contentRect } = entry;
        const [width, height] = [Math.floor(contentRect.width), Math.floor(contentRect.height)];
        return { width, height};
    }

    // Invokes the callback, either immediately or debounced
    let invokeCallback: (size: ElementSize) => void;

    if (options?.debounce) {
        const debounceInvokeCallback = useDebounceFn((size: ElementSize) => {
            callback(size, currentSize);
            currentSize = size;
        }, options.debounce);
        invokeCallback = (size: ElementSize) => {
            debounceInvokeCallback(size).catch((e) => log.error('Error invoking resize callback: %s', e));
        }
    } else {
        invokeCallback = (size: ElementSize) => {
            callback(size, currentSize);
            currentSize = size;
        };
    }

    let doInitialResize = true;

    const resizeObserver = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        if (entries.length === 0) {
            return;
        }
        const size = sizeFromEntry(entries[0]);
        if (doInitialResize) {
            // Set the current size and invoke the callback immediately.
            currentSize = size;
            doInitialResize = false;
            callback(size, undefined);
        } else {
            // Invoke immediate or debounced callback
            invokeCallback(size);
        }
    });

    resizeObserver.observe(element);

    return () => resizeObserver.disconnect();
}
